/**
 * ForecastWaterfall — ONE bar that fills toward the budget (quick-260921-f73).
 *
 * John, 2026-09-21: "make the deal waterfall more usable by only showing the
 * waterfall by commit and upside sums. show as a row that is filling with value
 * and then the target is a dotted line, and any gap is highlighted."
 *
 * So the per-deal stacked ECharts waterfall is gone. What renders instead:
 *
 *   [ Committed ████████ | Upside ▒▒▒▒ | gap ░░░░ ]  ┊ Budget
 *
 * - Committed = Closed Won + Commit + Most Likely at 1.00, across every ARR
 *   stream (Product New, Product Expansion, Managed Services) — the SAME
 *   `streams()` partition the equation strip reads, so the bar's total is the
 *   strip's Total to the dollar (D-13c).
 * - Upside = Best Case at the viewer's Upside %. Pipeline / no-position deals
 *   weigh 0 and so add no length (05.1 D-22).
 * - The budget is a dotted vertical line. When Committed + Upside falls short,
 *   the stretch between the bar's end and the line is a hatched red GAP; when
 *   it overshoots, the bar runs past the line and the overshoot reads green.
 *
 * The track is scaled to whichever is larger — the total or the budget — so
 * both always fit. Widths are inline percentages so a test can read them
 * without a canvas; there is no chart library here any more.
 *
 * `upsidePct` is RECEIVED from the section card (which received it from the
 * page). This component never sources the stored default itself (D-03).
 */
import { streams } from '../../lib/forecast-weighting.js';
import { formatAmount, pctOf, signed } from '../../lib/format.js';

import type { ForecastDeal } from '@saledash/shared';
import type { UpsidePct } from '@saledash/shared/forecast/weighting';

const COMMIT_COLOR = '#2851e6'; // brand-600
const UPSIDE_COLOR = '#0e9dc9'; // deep Adeptia cyan (validated pair with commit)
const GAP_STRIPE = 'repeating-linear-gradient(135deg, #fee2e2 0 6px, #fecaca 6px 12px)';
const OVER_STRIPE = 'repeating-linear-gradient(135deg, #dcfce7 0 6px, #bbf7d0 6px 12px)';

interface ForecastWaterfallProps {
  sectionKey: string;
  deals: ForecastDeal[];
  /** The period's ARR budget; null ⇒ no budget line and no gap. */
  target: number | null;
  /** The viewer Upside % (D-03), received from the section card. */
  upsidePct: UpsidePct;
}

/** Percent of the track, clamped so a rounding step can never overflow it. */
function pctOfTrack(value: number, scale: number): number {
  if (scale <= 0) return 0;
  return Math.max(0, Math.min(100, (value / scale) * 100));
}

export function ForecastWaterfall({
  sectionKey,
  deals,
  target,
  upsidePct,
}: ForecastWaterfallProps): React.JSX.Element {
  if (deals.length === 0) {
    return (
      <p
        className="py-10 text-center text-sm text-ink-500"
        data-testid={`forecast-waterfall-empty-${sectionKey}`}
      >
        No open deals this period.
      </p>
    );
  }

  const st = streams(deals, upsidePct);
  const committed =
    st.newBiz.won + st.newBiz.commit +
    st.expansion.won + st.expansion.commit +
    st.managed.won + st.managed.commit;
  const upside = st.newBiz.upside + st.expansion.upside + st.managed.upside;
  const total = committed + upside;

  const scale = Math.max(total, target ?? 0);
  const committedPct = pctOfTrack(committed, scale);
  const upsidePctW = pctOfTrack(upside, scale);
  const targetPct = target === null ? null : pctOfTrack(target, scale);
  const delta = target === null ? null : total - target;
  const gapPct = delta !== null && delta < 0 ? pctOfTrack(-delta, scale) : 0;
  const overPct = delta !== null && delta > 0 ? pctOfTrack(delta, scale) : 0;

  return (
    <div data-testid={`forecast-waterfall-${sectionKey}`} className="space-y-2">
      {/* Legend / figures — the three sums the bar is made of. */}
      <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-xs text-ink-500">
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COMMIT_COLOR }} />
          Committed{' '}
          <span className="font-semibold tabular-nums text-ink-900" data-testid={`forecast-waterfall-committed-${sectionKey}`}>
            {formatAmount(committed)}
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: UPSIDE_COLOR }} />
          Upside{' '}
          <span className="font-semibold tabular-nums text-ink-900" data-testid={`forecast-waterfall-upside-${sectionKey}`}>
            {formatAmount(upside)}
          </span>
        </span>
        <span>
          Total{' '}
          <span className="font-semibold tabular-nums text-ink-900" data-testid={`forecast-waterfall-total-${sectionKey}`}>
            {formatAmount(total)}
          </span>
        </span>
        {target !== null && (
          <span className="ml-auto">
            Budget{' '}
            <span className="font-semibold tabular-nums text-ink-900" data-testid={`forecast-waterfall-budget-${sectionKey}`}>
              {formatAmount(target)}
            </span>
          </span>
        )}
      </div>

      {/* The filling row. Room above for the budget label. */}
      <div className="relative pt-5">
        <div className="relative h-9 w-full overflow-hidden rounded-md bg-ink-100">
          <div
            className="absolute inset-y-0 left-0"
            style={{ width: `${String(committedPct)}%`, background: COMMIT_COLOR }}
            data-testid={`forecast-waterfall-bar-committed-${sectionKey}`}
            title={`Committed ${formatAmount(committed)}`}
          />
          <div
            className="absolute inset-y-0"
            style={{ left: `${String(committedPct)}%`, width: `${String(upsidePctW)}%`, background: UPSIDE_COLOR }}
            data-testid={`forecast-waterfall-bar-upside-${sectionKey}`}
            // eslint-disable-next-line @typescript-eslint/no-base-to-string -- UpsidePct may be the {product, managed} object form; existing title text kept as-is (behavior follow-up in SUMMARY)
            title={`Upside ${formatAmount(upside)} at ${String(upsidePct)}%`}
          />
          {gapPct > 0 && (
            <div
              className="absolute inset-y-0"
              style={{
                left: `${String(committedPct + upsidePctW)}%`,
                width: `${String(gapPct)}%`,
                background: GAP_STRIPE,
              }}
              data-testid={`forecast-waterfall-gap-${sectionKey}`}
              title={`Gap to budget ${formatAmount(-(delta ?? 0))}`}
            />
          )}
          {overPct > 0 && targetPct !== null && (
            <div
              className="absolute inset-y-0 opacity-70"
              style={{ left: `${String(targetPct)}%`, width: `${String(overPct)}%`, background: OVER_STRIPE }}
              data-testid={`forecast-waterfall-over-${sectionKey}`}
              title={`Over budget ${formatAmount(delta ?? 0)}`}
            />
          )}
        </div>

        {/* The budget line — dotted, full height of the row, label above. */}
        {targetPct !== null && (
          <div
            className="pointer-events-none absolute bottom-0 top-0 flex flex-col items-center"
            style={{ left: `${String(targetPct)}%`, transform: 'translateX(-50%)' }}
            data-testid={`forecast-waterfall-target-${sectionKey}`}
          >
            <span className="mb-0.5 whitespace-nowrap text-[0.66rem] font-semibold uppercase tracking-wider text-ink-700">
              Budget
            </span>
            <span aria-hidden className="w-0 flex-1 border-l-2 border-dotted border-ink-700" />
          </div>
        )}
      </div>

      {/* The read: how far from budget, both ways (D-06). */}
      {delta !== null && (
        <p
          className={`text-xs font-semibold tabular-nums ${delta < 0 ? 'text-bad-text' : 'text-good-text'}`}
          data-testid={`forecast-waterfall-delta-${sectionKey}`}
        >
          {delta < 0 ? `${signed(delta)} gap to budget` : `${signed(delta)} over budget`}
          <span className="ml-2 font-normal text-ink-500">{pctOf(total, target) ?? '—'} of budget</span>
        </p>
      )}
    </div>
  );
}
